import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback, useRef } from 'react';
import { SEED_CUSTOMERS, SEED_ORDERS, SEED_TRANSACTIONS } from '../data/seedData';
import { Customer, Order, Product, FinancialTransaction, Employee, Supplier, Prescription, OrderStatus, PaymentStatus } from '../types';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';
import { t } from '../lib/i18n';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface DataContextType {
  customers: Customer[];
  setCustomers: React.Dispatch<React.SetStateAction<Customer[]>>;
  addCustomer: (customer: Customer) => Promise<ActionResult>;
  updateCustomer: (customer: Customer) => Promise<ActionResult>;
  removeCustomer: (id: string) => Promise<ActionResult>;
  orders: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  addOrder: (order: Order, prescription?: { od: any; oe: any }) => Promise<ActionResult>;
  updateOrder: (order: Order) => Promise<ActionResult>;
  removeOrder: (id: string) => Promise<ActionResult>;
  products: Product[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  addProduct: (product: Omit<Product, 'id' | 'soldCount'>) => Promise<ActionResult>;
  updateProductStock: (id: string, quantity: number) => Promise<ActionResult>;
  removeProduct: (id: string) => Promise<ActionResult>;
  transactions: FinancialTransaction[];
  setTransactions: React.Dispatch<React.SetStateAction<FinancialTransaction[]>>;
  addTransaction: (transaction: FinancialTransaction) => Promise<ActionResult>;
  updateTransaction: (transaction: FinancialTransaction) => Promise<ActionResult>;
  removeTransaction: (id: string) => Promise<ActionResult>;
  suppliers: Supplier[];
  setSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>;
  addSupplier: (supplier: Supplier) => Promise<ActionResult>;
  removeSupplier: (id: string) => Promise<ActionResult>;
  addPrescription: (customerId: string, prescription: Prescription) => Promise<ActionResult>;
  updatePrescription: (prescription: Prescription) => Promise<ActionResult>;
  removePrescription: (id: string) => Promise<ActionResult>;
  employees: Employee[];
  isLoading: boolean;
}

type ActionResult = { success: boolean; error?: string };

// Sanitiza um campo numérico: remove caracteres inválidos e converte vazio em null
const sanitizeNumericField = (value: string | undefined | null): string | null => {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // Remove tudo que não seja dígito, ponto, vírgula, sinal de mais/menos
  const cleaned = trimmed.replace(/[^0-9.,+-]/g, '').replace(',', '.');
  if (cleaned === '' || cleaned === '+' || cleaned === '-' || cleaned === '.') return null;
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num.toString();
};

// Aplica sanitização em todos os campos numéricos de uma prescrição ocular
const sanitizeEyeData = (eye: Record<string, string> | null | undefined) => {
  if (!eye) return null;
  return {
    spherical: sanitizeNumericField(eye.spherical),
    cylinder: sanitizeNumericField(eye.cylinder),
    axis: sanitizeNumericField(eye.axis),
    add: sanitizeNumericField(eye.add),
    pd: sanitizeNumericField(eye.pd),
    height: sanitizeNumericField(eye.height),
  };
};

const INITIAL_DEMO_CUSTOMERS: Customer[] = SEED_CUSTOMERS;

const INITIAL_DEMO_PRODUCTS: Product[] = [
  {
    id: 'demo-prod-1',
    code: 'ARM-RB-2140',
    barcode: '7891000100011',
    name: 'Armação Ray-Ban Wayfarer Classic Acetato Preto',
    category: 'FRAME',
    brand: 'Ray-Ban',
    costPrice: 280,
    salesPrice: 650,
    stockLevel: 14,
    minStockLevel: 3,
    soldCount: 22,
  },
  {
    id: 'demo-prod-2',
    code: 'LEN-MF-DIG-167',
    barcode: '7891000100028',
    name: 'Lente Multifocal Digital Ultra HD 1.67 BlueCut',
    category: 'LENS',
    brand: 'OptiVision',
    costPrice: 320,
    salesPrice: 890,
    stockLevel: 8,
    minStockLevel: 4,
    soldCount: 35,
  },
  {
    id: 'demo-prod-3',
    code: 'SOL-OAK-HOL',
    barcode: '7891000100035',
    name: 'Óculos Solar Oakley Holbrook Polarizado Fosco',
    category: 'FRAME',
    brand: 'Oakley',
    costPrice: 340,
    salesPrice: 780,
    stockLevel: 6,
    minStockLevel: 2,
    soldCount: 18,
  },
  {
    id: 'demo-prod-4',
    code: 'ACS-SPRAY-60',
    barcode: '7891000100042',
    name: 'Kit Spray Limpa Lentes Antiembaçante 60ml + Microfibra',
    category: 'ACCESSORY',
    brand: 'OptiClean',
    costPrice: 8,
    salesPrice: 25,
    stockLevel: 45,
    minStockLevel: 15,
    soldCount: 110,
  },
];

const INITIAL_DEMO_ORDERS: Order[] = SEED_ORDERS;

const INITIAL_DEMO_TRANSACTIONS: FinancialTransaction[] = SEED_TRANSACTIONS;

const INITIAL_DEMO_SUPPLIERS: Supplier[] = [
  {
    id: 'demo-supp-1',
    name: 'Laboratório Central Vision',
    cnpj: '12.345.678/0001-90',
    contactName: 'Ricardo Laboratório',
    phone: '(11) 3222-1100',
    email: 'pedidos@centralvision.com.br',
  },
  {
    id: 'demo-supp-2',
    name: 'Luxottica Distribuidora Brasil',
    cnpj: '98.765.432/0001-10',
    contactName: 'Juliana Representante',
    phone: '(11) 4004-8000',
    email: 'atendimento@luxottica.com.br',
  },
];

const INITIAL_DEMO_EMPLOYEES: Employee[] = [
  {
    id: 'demo-emp-1',
    name: 'Mariana Vendedora',
    role: 'SELLER',
    salesTotal: 4850,
  },
  {
    id: 'demo-emp-2',
    name: 'Carlos Óptico Responsável',
    role: 'MANAGER',
    salesTotal: 6200,
  },
];

const DataContext = createContext<DataContextType | undefined>(undefined);

// --- Mappers (DB → App) ---

const mapCustomer = (db: any): Customer => ({
  id: db.id, name: db.name, phone: db.phone, email: db.email,
  cpf: db.cpf, rg: db.rg, birthDate: db.birth_date, gender: db.gender,
  profession: db.profession, address: db.address,
  creditLimit: db.credit_limit, creditStatus: db.credit_status,
  notes: db.notes, prescriptions: [],
});

const mapProduct = (db: any): Product => ({
  id: db.id, code: db.code, barcode: db.barcode, name: db.name,
  category: db.category, brand: db.brand,
  costPrice: db.cost_price, salesPrice: db.sales_price,
  stockLevel: db.stock_level, minStockLevel: db.min_stock_level,
  soldCount: db.sold_count,
});

const mapOrder = (db: any): Order => ({
  id: db.id, customerId: db.customer_id || db.customerId, customerName: db.customer_name || db.customerName || '',
  date: db.date || '', status: db.status,
  totalAmount: db.total_amount ?? db.totalAmount ?? 0, paidAmount: db.paid_amount ?? db.paidAmount ?? 0,
  paymentStatus: db.payment_status || db.paymentStatus, prescriptionId: db.prescription_id || db.prescriptionId || '',
  frameModel: db.frame_model || db.frameModel || '', frameNotes: db.frame_notes || db.frameNotes,
  lensType: db.lens_type || db.lensType || '', lensNotes: db.lens_notes || db.lensNotes,
  lab: db.lab, deliveryDate: db.delivery_date || db.deliveryDate || '',
  payments: db.payments || [],
});

const mapTransaction = (db: any): FinancialTransaction => ({
  id: db.id, date: db.date, description: db.description,
  type: db.type, category: db.category, amount: db.amount,
  status: db.status, paymentMethod: db.payment_method,
});

const mapSupplier = (db: any): Supplier => ({
  id: db.id, name: db.name, cnpj: db.cnpj,
  contactName: db.contact_name, phone: db.phone, email: db.email,
});

const mapPrescription = (db: any): Prescription => ({
  id: db.id, date: db.date, doctorName: db.doctor_name,
  od: { spherical: db.od_spherical, cylinder: db.od_cylinder, axis: db.od_axis, add: db.od_add, pd: db.od_pd, height: db.od_height },
  oe: { spherical: db.oe_spherical, cylinder: db.oe_cylinder, axis: db.oe_axis, add: db.oe_add, pd: db.oe_pd, height: db.oe_height },
  notes: db.notes,
});

const formatDbError = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== 'object') return fallback;
  const msg = (error as { message?: string }).message;
  if (!msg) return fallback;
  if (msg.includes('row-level security')) return t('errors.semPermissaoTenant');
  if (msg.includes('null value')) return t('errors.preenchaObrigatorios');
  return msg;
};

// --- Provider ---

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);

  const tenantId = user?.tenantId;

  // --- Initial Data Load ---
  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setIsLoading(true);

    if (tenantId === 'demo-tenant-id') {
      // Versão do seed — incrementar quando atualizar os dados iniciais para forçar reset do cache
      const SEED_VERSION = '2026-10-v3';
      const storedSeedVersion = localStorage.getItem('optisaas_seed_version');

      // Se a versão mudou, limpa todos os caches para usar os novos dados de seed
      if (storedSeedVersion !== SEED_VERSION) {
        localStorage.removeItem('optisaas_customers_demo-tenant-id');
        localStorage.removeItem('optisaas_orders_demo-tenant-id');
        localStorage.removeItem('optisaas_transactions_demo-tenant-id');
        localStorage.removeItem('optisaas_products_demo-tenant-id');
        localStorage.removeItem('optisaas_suppliers_demo-tenant-id');
        localStorage.setItem('optisaas_seed_version', SEED_VERSION);
      }

      const cachedProducts = localStorage.getItem('optisaas_products_demo-tenant-id');
      const cachedOrders = localStorage.getItem('optisaas_orders_demo-tenant-id');
      const cachedTransactions = localStorage.getItem('optisaas_transactions_demo-tenant-id');
      const cachedSuppliers = localStorage.getItem('optisaas_suppliers_demo-tenant-id');
      const cachedCustomers = localStorage.getItem('optisaas_customers_demo-tenant-id');

      const c = cachedCustomers ? JSON.parse(cachedCustomers) : INITIAL_DEMO_CUSTOMERS;
      const p = cachedProducts ? JSON.parse(cachedProducts) : INITIAL_DEMO_PRODUCTS;
      const o = cachedOrders ? JSON.parse(cachedOrders) : INITIAL_DEMO_ORDERS;
      const t = cachedTransactions ? JSON.parse(cachedTransactions) : INITIAL_DEMO_TRANSACTIONS;
      const s = cachedSuppliers ? JSON.parse(cachedSuppliers) : INITIAL_DEMO_SUPPLIERS;

      setCustomers(c);
      setProducts(p);
      setOrders(o);
      setTransactions(t);
      setSuppliers(s);
      setEmployees(INITIAL_DEMO_EMPLOYEES);

      if (!cachedCustomers) localStorage.setItem('optisaas_customers_demo-tenant-id', JSON.stringify(c));
      if (!cachedProducts) localStorage.setItem('optisaas_products_demo-tenant-id', JSON.stringify(p));
      if (!cachedOrders) localStorage.setItem('optisaas_orders_demo-tenant-id', JSON.stringify(o));
      if (!cachedTransactions) localStorage.setItem('optisaas_transactions_demo-tenant-id', JSON.stringify(t));
      if (!cachedSuppliers) localStorage.setItem('optisaas_suppliers_demo-tenant-id', JSON.stringify(s));

      setIsLoading(false);
      return;
    }

    // Carrega do cache local para exibição instantânea
    const cachedProducts = localStorage.getItem(`optisaas_products_${tenantId}`);
    const cachedOrders = localStorage.getItem(`optisaas_orders_${tenantId}`);
    const cachedTransactions = localStorage.getItem(`optisaas_transactions_${tenantId}`);
    const cachedSuppliers = localStorage.getItem(`optisaas_suppliers_${tenantId}`);
    const cachedCustomers = localStorage.getItem(`optisaas_customers_${tenantId}`);

    if (cachedProducts) setProducts(JSON.parse(cachedProducts));
    if (cachedOrders) setOrders(JSON.parse(cachedOrders));
    if (cachedTransactions) setTransactions(JSON.parse(cachedTransactions));
    if (cachedSuppliers) setSuppliers(JSON.parse(cachedSuppliers));
    if (cachedCustomers) setCustomers(JSON.parse(cachedCustomers));

    try {
      const [
        { data: custData }, { data: prodData }, { data: ordData },
        { data: transData }, { data: suppData }, { data: prescData }, { data: empData },
      ] = await Promise.all([
        supabase.from('customers').select('id, name, phone, email, cpf, rg, birth_date, gender, profession, address, credit_limit, credit_status, notes').eq('tenant_id', tenantId).is('deleted_at', null).order('created_at', { ascending: false }),
        supabase.from('products').select('id, code, barcode, name, category, brand, cost_price, sales_price, stock_level, min_stock_level, sold_count').eq('tenant_id', tenantId).is('deleted_at', null).order('name', { ascending: true }),
        supabase.from('orders').select('id, customer_id, customer_name, date, status, total_amount, paid_amount, payment_status, prescription_id, frame_model, frame_notes, lens_type, lens_notes, lab, delivery_date, payments').eq('tenant_id', tenantId).is('deleted_at', null).order('date', { ascending: false }),
        supabase.from('financial_transactions').select('id, date, description, type, category, amount, status, payment_method').eq('tenant_id', tenantId).is('deleted_at', null).order('date', { ascending: false }),
        supabase.from('suppliers').select('id, name, cnpj, contact_name, phone, email').eq('tenant_id', tenantId).is('deleted_at', null).order('name', { ascending: true }),
        supabase.from('prescriptions').select('id, date, doctor_name, od_spherical, od_cylinder, od_axis, od_add, od_pd, od_height, oe_spherical, oe_cylinder, oe_axis, oe_add, oe_pd, oe_height, notes, customer_id').eq('tenant_id', tenantId).is('deleted_at', null).order('created_at', { ascending: false }),
        supabase.from('profiles').select('id, name, role').eq('tenant_id', tenantId).in('role', ['OWNER', 'MANAGER', 'SELLER']),
      ]);

      if (prodData) {
        const mapped = prodData.map(mapProduct);
        setProducts(mapped);
        localStorage.setItem(`optisaas_products_${tenantId}`, JSON.stringify(mapped));
      }
      if (ordData) {
        const mapped = ordData.map(mapOrder);
        setOrders(mapped);
        localStorage.setItem(`optisaas_orders_${tenantId}`, JSON.stringify(mapped));
      }
      if (transData) {
        const mapped = transData.map(mapTransaction);
        setTransactions(mapped);
        localStorage.setItem(`optisaas_transactions_${tenantId}`, JSON.stringify(mapped));
      }
      if (suppData) {
        const mapped = suppData.map(mapSupplier);
        setSuppliers(mapped);
        localStorage.setItem(`optisaas_suppliers_${tenantId}`, JSON.stringify(mapped));
      }

      if (custData) {
        const prescriptions = prescData?.map(mapPrescription) ?? [];
        const mappedCustomers = custData.map(c => {
          const mapped = mapCustomer(c);
          mapped.prescriptions = prescriptions.filter(p => (prescData?.find(pd => pd.id === p.id)?.customer_id) === c.id);
          return mapped;
        });
        setCustomers(mappedCustomers);
        localStorage.setItem(`optisaas_customers_${tenantId}`, JSON.stringify(mappedCustomers));
      }

      if (empData) {
        const mappedEmployees: Employee[] = empData.map((p: any) => ({
          id: p.id,
          name: p.name,
          role: p.role,
          salesTotal: 0,
        }));
        const salesByEmployee: Record<string, number> = {};
        ordData?.forEach((o: any) => {
          const seller = empData.find((e: any) => e.name === o.seller_name);
          if (seller) {
            salesByEmployee[seller.id] = (salesByEmployee[seller.id] || 0) + (o.total_amount || 0);
          }
        });
        mappedEmployees.forEach(emp => {
          emp.salesTotal = salesByEmployee[emp.id] || 0;
        });
        setEmployees(mappedEmployees);
      }
  } catch (err) {
    logger.error('DataContext', 'loadData error', { error: err });
  } finally {
    setIsLoading(false);
  }
  }, [tenantId]);

  // --- Realtime Handlers ---
  const handleRealtimeChange = useCallback(<T,>(
    payload: RealtimePostgresChangesPayload<any>,
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    mapper: (db: any) => T,
    idField: string = 'id',
  ) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;

    switch (eventType) {
      case 'INSERT':
        if (newRecord && !newRecord.deleted_at) setter(prev => [mapper(newRecord), ...prev]);
        break;
      case 'UPDATE':
        if (newRecord) {
          if (newRecord.deleted_at) {
            // Soft delete: remove from state
            setter(prev => prev.filter(item => (item as any)[idField] !== newRecord.id));
          } else {
            setter(prev => {
              const exists = prev.some(item => (item as any)[idField] === newRecord.id);
              if (exists) return prev.map(item => (item as any)[idField] === newRecord.id ? mapper(newRecord) : item);
              return [mapper(newRecord), ...prev]; // Restore: add back
            });
          }
        }
        break;
      case 'DELETE':
        if (oldRecord) setter(prev => prev.filter(item => (item as any)[idField] !== oldRecord.id));
        break;
    }
  }, []);

  // --- Subscribe to Realtime ---
  useEffect(() => {
    if (!isAuthenticated || !tenantId) {
      setCustomers([]); setOrders([]); setProducts([]);
      setTransactions([]); setSuppliers([]);
      return;
    }

    if (tenantId === 'demo-tenant-id') {
      loadData();
      return;
    }

    loadData();

    const channel = supabase
      .channel(`tenant-${tenantId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'customers', filter: `tenant_id=eq.${tenantId}` },
        (p) => handleRealtimeChange(p, setCustomers, mapCustomer))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `tenant_id=eq.${tenantId}` },
        (p) => handleRealtimeChange(p, setOrders, mapOrder))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products', filter: `tenant_id=eq.${tenantId}` },
        (p) => handleRealtimeChange(p, setProducts, mapProduct))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'financial_transactions', filter: `tenant_id=eq.${tenantId}` },
        (p) => handleRealtimeChange(p, setTransactions, mapTransaction))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'suppliers', filter: `tenant_id=eq.${tenantId}` },
        (p) => handleRealtimeChange(p, setSuppliers, mapSupplier))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'prescriptions', filter: `tenant_id=eq.${tenantId}` },
        (p) => {
          const { eventType, new: newRecord, old: oldRecord } = p;
          
          // Tratamento granular para receitas para evitar recarregamento total do app
          if (eventType === 'INSERT' && newRecord) {
            const presc = mapPrescription(newRecord);
            // Adiciona a nova receita ao cliente específico
            setCustomers(prev => prev.map(c => c.id === newRecord.customer_id ? { ...c, prescriptions: [presc, ...c.prescriptions] } : c));
          } else if (eventType === 'UPDATE' && newRecord) {
            const presc = mapPrescription(newRecord);
            // Atualiza a receita existente no cliente específico
            setCustomers(prev => prev.map(c => c.id === newRecord.customer_id ? { ...c, prescriptions: c.prescriptions.map(pr => pr.id === presc.id ? presc : pr) } : c));
          } else if (eventType === 'DELETE' && oldRecord) {
            // Remove a receita do estado local
            setCustomers(prev => prev.map(c => ({ ...c, prescriptions: c.prescriptions.filter(pr => pr.id !== oldRecord.id) })));
          }
        })
      .subscribe();

    channelRef.current = channel;
    return () => { supabase.removeChannel(channel); channelRef.current = null; };
  }, [isAuthenticated, tenantId, loadData, handleRealtimeChange]);

  // --- CRUD Actions ---

const addCustomer = async (customer: Customer): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    const newCust: Customer = {
      ...customer,
      id: customer.id || `demo-cust-${Date.now()}`,
      prescriptions: customer.prescriptions || [],
    };
    setCustomers(prev => {
      const updated = [newCust, ...prev];
      localStorage.setItem('optisaas_customers_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('customers').insert([{
    tenant_id: tenantId, name: customer.name, phone: customer.phone,
    email: customer.email, cpf: customer.cpf, rg: customer.rg,
    birth_date: customer.birthDate, gender: customer.gender,
    profession: customer.profession, address: customer.address,
    credit_limit: customer.creditLimit, credit_status: customer.creditStatus,
    notes: customer.notes,
  }]).select().single();
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoSalva')) };
  return { success: true };
};

const updateCustomer = async (customer: Customer): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setCustomers(prev => {
      const updated = prev.map(c => c.id === customer.id ? customer : c);
      localStorage.setItem('optisaas_customers_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('customers').update({
    name: customer.name, phone: customer.phone, email: customer.email,
    cpf: customer.cpf, profession: customer.profession, address: customer.address,
    birth_date: customer.birthDate, gender: customer.gender, rg: customer.rg,
    credit_limit: customer.creditLimit, credit_status: customer.creditStatus,
    notes: customer.notes,
  }).eq('id', customer.id).eq('tenant_id', tenantId);
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoAtualizar')) };
  setCustomers(prev => prev.map(c => c.id === customer.id ? customer : c));
  return { success: true };
};

const addOrder = async (order: Order, prescription?: { od: any; oe: any }): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    const newOrderId = order.id || `OS-${Math.floor(1000 + Math.random() * 9000)}`;
    let prescId = order.prescriptionId;

    if (prescription && (prescription.od || prescription.oe)) {
      prescId = `demo-presc-${Date.now()}`;
      const newPresc: Prescription = {
        id: prescId,
        date: order.date || new Date().toISOString().split('T')[0],
        doctorName: 'Dr. Oftalmologista',
        od: prescription.od,
        oe: prescription.oe,
      };
      setCustomers(prev => {
        const updated = prev.map(c => c.id === order.customerId ? { ...c, prescriptions: [newPresc, ...(c.prescriptions || [])] } : c);
        localStorage.setItem('optisaas_customers_demo-tenant-id', JSON.stringify(updated));
        return updated;
      });
    }

    const newOrder: Order = {
      ...order,
      id: newOrderId,
      prescriptionId: prescId,
    };

    setOrders(prev => {
      const updated = [newOrder, ...prev];
      localStorage.setItem('optisaas_orders_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const safeOd = sanitizeEyeData(prescription?.od);
  const safeOe = sanitizeEyeData(prescription?.oe);
  const { error } = await supabase.rpc('create_order_with_prescription', {
    p_customer_id: order.customerId, p_tenant_id: tenantId,
    p_customer_name: order.customerName || 'Cliente',
    p_date: order.date, p_status: order.status,
    p_total_amount: order.totalAmount, p_paid_amount: order.paidAmount,
    p_payment_status: order.paymentStatus,
    p_frame_model: order.frameModel, p_frame_notes: order.frameNotes,
    p_lens_type: order.lensType, p_lens_notes: order.lensNotes || null,
    p_lab: order.lab, p_delivery_date: order.deliveryDate,
    p_od: safeOd, p_oe: safeOe,
  });
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoSalva')) };
  return { success: true };
};

const updateOrder = async (order: Order): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setOrders(prev => {
      const updated = prev.map(o => o.id === order.id ? order : o);
      localStorage.setItem('optisaas_orders_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('orders').update({
    status: order.status, paid_amount: order.paidAmount,
    payment_status: order.paymentStatus,
    frame_model: order.frameModel, frame_notes: order.frameNotes,
    lens_type: order.lensType, lens_notes: order.lensNotes,
    lab: order.lab, delivery_date: order.deliveryDate,
    payments: order.payments || [],
  }).eq('id', order.id).eq('tenant_id', tenantId);
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoAtualizar')) };
  setOrders(prev => prev.map(o => o.id === order.id ? order : o));
  return { success: true };
};

const removeOrder = async (id: string): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setOrders(prev => {
      const updated = prev.filter(o => o.id !== id);
      localStorage.setItem('optisaas_orders_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('orders').update({ deleted_at: new Date().toISOString() }).eq('id', id).eq('tenant_id', tenantId);
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoExcluir')) };
  return { success: true };
};

const removeCustomer = async (id: string): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setCustomers(prev => {
      const updated = prev.filter(c => c.id !== id);
      localStorage.setItem('optisaas_customers_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('customers').update({ deleted_at: new Date().toISOString() }).eq('id', id).eq('tenant_id', tenantId);
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoExcluir')) };
  return { success: true };
};

const addProduct = async (product: Omit<Product, 'id' | 'soldCount'>): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    const newProd: Product = {
      ...product,
      id: `demo-prod-${Date.now()}`,
      soldCount: 0,
    };
    setProducts(prev => {
      const updated = [newProd, ...prev];
      localStorage.setItem('optisaas_products_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('products').insert([{
    tenant_id: tenantId, code: product.code, barcode: product.barcode,
    name: product.name, category: product.category, brand: product.brand,
    cost_price: product.costPrice, sales_price: product.salesPrice,
    stock_level: product.stockLevel, min_stock_level: product.minStockLevel,
    sold_count: 0,
  }]).select().single();
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoSalva')) };
  return { success: true };
};

const updateProductStock = async (id: string, quantity: number): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setProducts(prev => {
      const updated = prev.map(p => p.id === id ? { ...p, stockLevel: p.stockLevel + quantity } : p);
      localStorage.setItem('optisaas_products_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.rpc('increment_stock', { product_id: id, qty: quantity });
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoAtualizar')) };
  setProducts(prev => prev.map(p => p.id === id ? { ...p, stockLevel: p.stockLevel + quantity } : p));
  return { success: true };
};

const removeProduct = async (id: string): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setProducts(prev => {
      const updated = prev.filter(p => p.id !== id);
      localStorage.setItem('optisaas_products_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('products').update({ deleted_at: new Date().toISOString() }).eq('id', id).eq('tenant_id', tenantId);
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoExcluir')) };
  return { success: true };
};

const addTransaction = async (transaction: FinancialTransaction): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    const newTx: FinancialTransaction = {
      ...transaction,
      id: transaction.id || `demo-tx-${Date.now()}`,
    };
    setTransactions(prev => {
      const updated = [newTx, ...prev];
      localStorage.setItem('optisaas_transactions_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('financial_transactions').insert([{
    tenant_id: tenantId, date: transaction.date, description: transaction.description,
    type: transaction.type, category: transaction.category,
    amount: transaction.amount, status: transaction.status,
    payment_method: transaction.paymentMethod,
  }]).select().single();
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoSalva')) };
  return { success: true };
};

const removeTransaction = async (id: string): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setTransactions(prev => {
      const updated = prev.filter(t => t.id !== id);
      localStorage.setItem('optisaas_transactions_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('financial_transactions').update({ deleted_at: new Date().toISOString() }).eq('id', id).eq('tenant_id', tenantId);
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoExcluir')) };
  return { success: true };
};

const updateTransaction = async (transaction: FinancialTransaction): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setTransactions(prev => {
      const updated = prev.map(t => t.id === transaction.id ? transaction : t);
      localStorage.setItem('optisaas_transactions_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('financial_transactions').update({
    date: transaction.date,
    description: transaction.description,
    type: transaction.type,
    category: transaction.category,
    amount: transaction.amount,
    status: transaction.status,
    payment_method: transaction.paymentMethod,
  }).eq('id', transaction.id).eq('tenant_id', tenantId);
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoAtualizar')) };
  setTransactions(prev => prev.map(t => t.id === transaction.id ? transaction : t));
  return { success: true };
};

const addSupplier = async (supplier: Supplier): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    const newSupp: Supplier = {
      ...supplier,
      id: supplier.id || `demo-supp-${Date.now()}`,
    };
    setSuppliers(prev => {
      const updated = [newSupp, ...prev];
      localStorage.setItem('optisaas_suppliers_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('suppliers').insert([{
    tenant_id: tenantId, name: supplier.name, cnpj: supplier.cnpj,
    contact_name: supplier.contactName, phone: supplier.phone, email: supplier.email,
  }]).select().single();
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoSalva')) };
  return { success: true };
};

const removeSupplier = async (id: string): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setSuppliers(prev => {
      const updated = prev.filter(s => s.id !== id);
      localStorage.setItem('optisaas_suppliers_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('suppliers').update({ deleted_at: new Date().toISOString() }).eq('id', id).eq('tenant_id', tenantId);
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoExcluir')) };
  return { success: true };
};

const removePrescription = async (id: string): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setCustomers(prev => {
      const updated = prev.map(c => ({
        ...c,
        prescriptions: (c.prescriptions || []).filter(p => p.id !== id),
      }));
      localStorage.setItem('optisaas_customers_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const { error } = await supabase.from('prescriptions').update({ deleted_at: new Date().toISOString() }).eq('id', id).eq('tenant_id', tenantId);
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoExcluir')) };
  return { success: true };
};

const addPrescription = async (customerId: string, prescription: Prescription): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    const newPresc: Prescription = {
      ...prescription,
      id: prescription.id || `demo-presc-${Date.now()}`,
    };
    setCustomers(prev => {
      const updated = prev.map(c => c.id === customerId ? { ...c, prescriptions: [newPresc, ...(c.prescriptions || [])] } : c);
      localStorage.setItem('optisaas_customers_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const safeOd = sanitizeEyeData(prescription.od as unknown as Record<string, string>);
  const safeOe = sanitizeEyeData(prescription.oe as unknown as Record<string, string>);
  const { error } = await supabase.from('prescriptions').insert([{
    customer_id: customerId, tenant_id: tenantId,
    date: prescription.date, doctor_name: prescription.doctorName,
    od_spherical: safeOd?.spherical, od_cylinder: safeOd?.cylinder,
    od_axis: safeOd?.axis, od_add: safeOd?.add,
    od_pd: safeOd?.pd, od_height: safeOd?.height,
    oe_spherical: safeOe?.spherical, oe_cylinder: safeOe?.cylinder,
    oe_axis: safeOe?.axis, oe_add: safeOe?.add,
    oe_pd: safeOe?.pd, oe_height: safeOe?.height,
    notes: prescription.notes,
  }]).select().single();
  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoSalva')) };
  return { success: true };
};

const updatePrescription = async (prescription: Prescription): Promise<ActionResult> => {
  if (!tenantId) return { success: false, error: t('errors.sessaoExpirada') };

  if (tenantId === 'demo-tenant-id') {
    setCustomers(prev => {
      const updated = prev.map(c => ({
        ...c,
        prescriptions: (c.prescriptions || []).map(p => p.id === prescription.id ? prescription : p),
      }));
      localStorage.setItem('optisaas_customers_demo-tenant-id', JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  }

  const safeOd = sanitizeEyeData(prescription.od as unknown as Record<string, string>);
  const safeOe = sanitizeEyeData(prescription.oe as unknown as Record<string, string>);
  const { error } = await supabase.from('prescriptions').update({
    date: prescription.date,
    doctor_name: prescription.doctorName,
    od_spherical: safeOd?.spherical,
    od_cylinder: safeOd?.cylinder,
    od_axis: safeOd?.axis,
    od_add: safeOd?.add,
    od_pd: safeOd?.pd,
    od_height: safeOd?.height,
    oe_spherical: safeOe?.spherical,
    oe_cylinder: safeOe?.cylinder,
    oe_axis: safeOe?.axis,
    oe_add: safeOe?.add,
    oe_pd: safeOe?.pd,
    oe_height: safeOe?.height,
    notes: prescription.notes,
  }).eq('id', prescription.id).eq('tenant_id', tenantId);

  if (error) return { success: false, error: formatDbError(error, t('errors.erroAoAtualizar')) };

  setCustomers(prev => prev.map(c => {
    const hasPresc = c.prescriptions.some(p => p.id === prescription.id);
    if (!hasPresc) return c;
    return {
      ...c,
      prescriptions: c.prescriptions.map(p => p.id === prescription.id ? prescription : p)
    };
  }));

  return { success: true };
};


  return (
    <DataContext.Provider value={{
      customers, setCustomers, addCustomer, updateCustomer, removeCustomer,
      orders, setOrders, addOrder, updateOrder, removeOrder,
      products, setProducts, updateProductStock, addProduct, removeProduct,
      transactions, setTransactions, addTransaction, updateTransaction, removeTransaction,
      suppliers, setSuppliers, addSupplier, removeSupplier,
      addPrescription, updatePrescription, removePrescription, employees, isLoading,
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) throw new Error('useData must be used within a DataProvider');
  return context;
};
// aria-label: added for accessibility compliance
